import { useState } from 'react';
import { RefreshCw, UserSearch } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  useAdminGetStripeCustomers,
  useAdminSyncStripeCustomers,
} from '@/api/auth/admin/admin';
import { SyncCustomerDialog } from './SyncCustomerDialog';

/**
 * Admin page for managing Stripe customers
 */
export default function StripeCustomersPage() {
  const [page, setPage] = useState(1);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const pageSize = 20;

  const {
    data: customersData,
    isLoading,
    error,
    refetch,
  } = useAdminGetStripeCustomers({ page, pageSize });

  const syncMutation = useAdminSyncStripeCustomers({
    mutation: {
      onSuccess: (data) => {
        toast.success('Sync started', {
          description: `Orchestration ${data.data.instanceId} is running. Status: ${data.data.runtimeStatus}`,
        });
        // Refetch after a delay to show updated data
        setTimeout(() => refetch(), 3000);
      },
      onError: (error) => {
        toast.error('Sync failed', {
          description: (error as Error)?.message || 'Unknown error',
        });
      },
    },
  });

  const customers = customersData?.data?.items || [];
  const totalCount = customersData?.data?.totalCount || 0;
  const hasMore = customersData?.data?.hasMore || false;

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Stripe Customers</h1>
          <p className="text-muted-foreground">
            Manage synced Stripe customers ({totalCount} total)
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setSyncDialogOpen(true)}
          >
            <UserSearch className="mr-2 h-4 w-4" />
            Sync Customer
          </Button>
          <Button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
            Sync All
          </Button>
        </div>
      </div>

      <SyncCustomerDialog
        open={syncDialogOpen}
        onOpenChange={setSyncDialogOpen}
        onSyncStarted={() => {
          // Refetch after a delay to show updated data
          setTimeout(() => refetch(), 3000);
        }}
      />

      {error ? (
        <div className="text-center text-destructive py-8">
          <p>Failed to load customers: {(error as Error)?.message || 'Unknown error'}</p>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer ID</TableHead>
                <TableHead>User ID</TableHead>
                <TableHead>Organization ID</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton className="h-4 w-[180px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[40px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[40px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
                  </TableRow>
                ))
              ) : customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No customers found. Click "Sync from Stripe" to import customers.
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow key={customer.stripeCustomerId}>
                    <TableCell className="font-mono text-sm">
                      {customer.stripeCustomerId}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {customer.userId ? (
                        <span className="truncate max-w-[150px] inline-block" title={customer.userId}>
                          {customer.userId.substring(0, 8)}...
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {customer.organizationId ? (
                        <span className="truncate max-w-[150px] inline-block" title={customer.organizationId}>
                          {customer.organizationId.substring(0, 8)}...
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {customer.billingCountry || '-'}
                    </TableCell>
                    <TableCell className="uppercase">
                      {customer.currency || '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(customer.createdAt)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalCount > pageSize && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="py-2 px-3 text-sm text-muted-foreground">
                Page {page}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
